import { useState, useEffect, useCallback, useRef } from 'react';
import { IconPlus, IconPencil, IconTrash, IconUpload } from '@tabler/icons-react';
import type { StatPanel, StatDataPoint } from '@2bforest/common';
import {
  listPanels,
  deletePanel,
  listDataPointFields,
  parseExcelFile,
  bulkReplaceDataPoints,
} from '../services/statisticsService';
import StatPanelModal from '../components/StatPanelModal';

type ActiveTab = 'panels' | 'data_points';

const DISPLAY_TYPE_LABELS: Record<string, string> = {
  infographic: 'Infographic',
  bar_chart: 'Bar Chart',
  stacked_bar_chart: 'Stacked Bar Chart',
  pie_chart: 'Pie Chart',
  table: 'Table',
};

export default function StatisticsPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('panels');

  // --- Panels tab state ---
  const [panels, setPanels] = useState<StatPanel[]>([]);
  const [loadingPanels, setLoadingPanels] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingPanel, setEditingPanel] = useState<StatPanel | null>(null);

  // --- Data points tab state ---
  const [temas, setTemas] = useState<string[]>([]);
  const [loadingTemas, setLoadingTemas] = useState(false);

  // Import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importedPoints, setImportedPoints] = useState<Omit<StatDataPoint, 'id'>[]>([]);
  const [importConfirmVisible, setImportConfirmVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState('');
  const [importError, setImportError] = useState('');
  const [lastImportByTema, setLastImportByTema] = useState<Record<string, number> | null>(null);

  // Load panels
  const loadPanels = useCallback(async () => {
    setLoadingPanels(true);
    try {
      const result = await listPanels();
      setPanels(result);
    } catch (err) {
      console.error('Failed to load panels:', err);
      alert('Failed to load panels.');
    } finally {
      setLoadingPanels(false);
    }
  }, []);

  // Load distinct tema values
  const loadTemas = useCallback(async () => {
    setLoadingTemas(true);
    try {
      const fields = await listDataPointFields();
      setTemas(fields.theme ?? []);
    } catch (err) {
      console.error('Failed to load temas:', err);
    } finally {
      setLoadingTemas(false);
    }
  }, []);

  useEffect(() => {
    loadPanels();
  }, [loadPanels]);

  useEffect(() => {
    if (activeTab === 'data_points') {
      loadTemas();
    }
  }, [activeTab, loadTemas]);

  async function handleSavePanel(saved: StatPanel) {
    // Modal handles create/update; just refresh the list
    void saved;
    setShowModal(false);
    setEditingPanel(null);
    await loadPanels();
  }

  function handleNewPanel() {
    setEditingPanel(null);
    setShowModal(true);
  }

  function handleEditPanel(panel: StatPanel) {
    setEditingPanel(panel);
    setShowModal(true);
  }

  async function handleDeletePanel(panel: StatPanel) {
    if (!confirm(`Delete panel "${panel.title}"? This cannot be undone.`)) return;
    try {
      await deletePanel(panel.id);
      await loadPanels();
    } catch (err) {
      console.error('Failed to delete panel:', err);
      alert('Failed to delete panel.');
    }
  }

  function handleImportClick() {
    setImportSuccess('');
    setImportError('');
    setImportConfirmVisible(false);
    setImportedPoints([]);
    fileInputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';
    setImportSuccess('');
    setImportError('');
    try {
      const points = await parseExcelFile(file);
      setImportedPoints(points);
      setImportConfirmVisible(true);
    } catch (err) {
      console.error('Failed to parse Excel file:', err);
      setImportError(err instanceof Error ? err.message : 'Failed to parse file.');
    }
  }

  async function handleConfirmImport() {
    setImporting(true);
    setImportError('');
    try {
      await bulkReplaceDataPoints(importedPoints);

      // Build counts by theme from imported array
      const counts: Record<string, number> = {};
      for (const p of importedPoints) {
        const t = p.theme ?? '';
        counts[t] = (counts[t] ?? 0) + 1;
      }
      setLastImportByTema(counts);
      setTemas(Object.keys(counts).sort());

      setImportSuccess(
        `Imported ${importedPoints.length} data points across ${Object.keys(counts).length} categories.`
      );
      setImportConfirmVisible(false);
      setImportedPoints([]);
    } catch (err) {
      console.error('Import failed:', err);
      setImportError(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
    }
  }

  function handleCancelImport() {
    setImportConfirmVisible(false);
    setImportedPoints([]);
  }

  // Compute theme counts for display
  const temaRows: Array<{ tema: string; count: number | null }> = temas.map(t => ({
    tema: t,
    count: lastImportByTema ? (lastImportByTema[t] ?? null) : null,
  }));
  const totalImported = lastImportByTema
    ? Object.values(lastImportByTema).reduce((sum, n) => sum + n, 0)
    : null;

  return (
    <div className="page-wrapper">
      {showModal && (
        <>
          <div className="modal-backdrop fade show" />
          <StatPanelModal
            panel={editingPanel}
            onSave={handleSavePanel}
            onClose={() => {
              setShowModal(false);
              setEditingPanel(null);
            }}
          />
        </>
      )}

      <div className="page-header d-print-none">
        <div className="row g-2 align-items-center">
          <div className="col">
            <div className="page-pretitle">Content</div>
            <h2 className="page-title">Statistics</h2>
          </div>
          {activeTab === 'panels' && (
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleNewPanel}>
                <IconPlus size={16} className="me-1" />
                New Panel
              </button>
            </div>
          )}
          {activeTab === 'data_points' && (
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleImportClick}>
                <IconUpload size={16} className="me-1" />
                Import Excel
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          )}
        </div>
      </div>

      <div className="page-body">
        {/* Tabs */}
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <a
              className={`nav-link${activeTab === 'panels' ? ' active' : ''}`}
              href="#"
              onClick={e => {
                e.preventDefault();
                setActiveTab('panels');
              }}
            >
              Panels
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link${activeTab === 'data_points' ? ' active' : ''}`}
              href="#"
              onClick={e => {
                e.preventDefault();
                setActiveTab('data_points');
              }}
            >
              Data Points
            </a>
          </li>
        </ul>

        {/* Panels tab */}
        {activeTab === 'panels' && (
          <div className="card">
            <div className="card-body p-0">
              {loadingPanels ? (
                <div className="p-4 text-center text-muted">Loading…</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Order</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Type</th>
                        <th style={{ width: 100 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {panels.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No panels yet. Click &quot;New Panel&quot; to create one.
                          </td>
                        </tr>
                      )}
                      {[...panels]
                        .sort((a, b) => a.order - b.order)
                        .map(p => (
                          <tr key={p.id}>
                            <td>{p.order}</td>
                            <td>
                              {p.title}
                              {p.subtitle && <div className="text-muted small">{p.subtitle}</div>}
                            </td>
                            <td>{p.category}</td>
                            <td>
                              <span className="badge bg-secondary-lt">
                                {DISPLAY_TYPE_LABELS[p.displayType] ?? p.displayType}
                              </span>
                            </td>
                            <td>
                              <div className="btn-list flex-nowrap">
                                <button
                                  className="btn btn-icon"
                                  title="Edit"
                                  onClick={() => handleEditPanel(p)}
                                >
                                  <IconPencil size={16} />
                                </button>
                                <button
                                  className="btn btn-icon text-danger"
                                  title="Delete"
                                  onClick={() => handleDeletePanel(p)}
                                >
                                  <IconTrash size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Data Points tab */}
        {activeTab === 'data_points' && (
          <div className="card">
            <div className="card-body">
              {importConfirmVisible && (
                <div className="alert alert-info mb-3" role="alert">
                  <p className="mb-2">
                    Found <strong>{importedPoints.length}</strong> data points across{' '}
                    <strong>{new Set(importedPoints.map(p => p.theme)).size}</strong> categories.
                    This will <strong>REPLACE</strong> all existing data points. Confirm?
                  </p>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={handleConfirmImport}
                      disabled={importing}
                    >
                      {importing ? 'Importing…' : 'Confirm Import'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={handleCancelImport}
                      disabled={importing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {importSuccess && (
                <div className="alert alert-success mb-3" role="alert">
                  {importSuccess}
                </div>
              )}
              {importError && (
                <div className="alert alert-danger mb-3" role="alert">
                  {importError}
                </div>
              )}

              {loadingTemas ? (
                <div className="text-center text-muted py-4">Loading…</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-vcenter table-sm">
                    <thead>
                      <tr>
                        <th>Tema</th>
                        <th style={{ width: 120 }}>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {temaRows.length === 0 && (
                        <tr>
                          <td colSpan={2} className="text-center text-muted py-4">
                            No data points found. Import an Excel file to populate data.
                          </td>
                        </tr>
                      )}
                      {temaRows.map(row => (
                        <tr key={row.tema}>
                          <td>{row.tema}</td>
                          <td>
                            {row.count !== null ? row.count : <span className="text-muted">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {temaRows.length > 0 && (
                      <tfoot>
                        <tr>
                          <td>
                            <strong>Total</strong>
                          </td>
                          <td>
                            <strong>
                              {totalImported !== null ? (
                                totalImported
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </strong>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                  {lastImportByTema === null && temaRows.length > 0 && (
                    <p className="text-muted small mt-2">
                      Import Excel to see per-category counts.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
